import { Link } from 'react-router-dom'
import { 
  ChevronLeft, 
  ChevronRight, 
  Play, 
  Swords, 
  Compass, 
  Puzzle, 
  Building2, 
  MoreHorizontal,
  Gamepad2,
  Search,
  Rocket,
  Users,
  ShieldCheck,
  Wallet,
  Globe,
  Network,
  CheckCircle2,
  ChevronDown,
  Target,
  Zap
} from 'lucide-react'
import { useState, useCallback, useEffect } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import { getFeaturedGames, getTrendingGames, getPlayableGames, getGameUrl } from '@/data/games'
import { getAllBlogs } from '@/data/blogs'
import { SEO, WebsiteStructuredData } from '@/components/seo/SEO'

// Get games from centralized data
const featuredGames = getFeaturedGames()
const trendingGames = getTrendingGames()
const playableGames = getPlayableGames()
// Duplicate playable games for seamless infinite scroll
const infinitePlayableGames = [...playableGames, ...playableGames, ...playableGames]

// Genres data
const genres = [
  { name: 'Action', icon: Swords },
  { name: 'Strategy', icon: Target },
  { name: 'Adventure', icon: Compass },
  { name: 'Puzzle', icon: Puzzle },
  { name: 'Simulation', icon: Building2 },
  { name: 'All Genres', icon: MoreHorizontal }
]

// FAQ data
const faqs = [
  {
    question: 'Is Polytoria really free to play?',
    answer: 'Yes! Polytoria is completely free to play. You can access thousands of community-created experiences, customize your avatar, and join the social community without ever spending a dime. We offer an optional premium subscription for users who want to support the platform and unlock extra features.'
  },
  {
    question: 'Do I need a high-end PC?',
    answer: 'Not at all. Our engine is optimized to run smoothly on standard laptops, Chromebooks, and even older hardware. As long as you have a modern web browser and a stable internet connection, you can play.'
  },
  {
    question: 'How do I start building games?',
    answer: 'Simply visit our \'Create\' section and download the Polytoria Studio. It\'s an intuitive but powerful tool that allows you to build worlds, script interactions using Lua, and publish your work instantly to the entire platform.'
  }
]

// FAQ Item Component
function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="bg-card-dark border border-border-dark rounded-2xl overflow-hidden">
      <button 
        className="flex items-center justify-between w-full p-6 text-left text-white font-bold hover:bg-white/5 transition-colors"
        onClick={onToggle}
      >
        {question}
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-6 pb-6 text-slate-400 text-sm leading-relaxed border-t border-border-dark/50 pt-4">
          {answer}
        </div>
      )}
    </div>
  )
}

export function HomePage() {
  const [openFaq, setOpenFaq] = useState(0)

  const latestBlogs = getAllBlogs().slice(0, 4)
  
  // Embla Carousel setup with autoplay for Featured section
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', skipSnaps: false },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  )
  
  // Embla Carousel for Instant Play section - uses duplicated items for seamless scroll
  const [instantPlayEmblaRef, instantPlayEmblaApi] = useEmblaCarousel(
    { loop: false, align: 'start', slidesToScroll: 1, dragFree: true, containScroll: false },
    [Autoplay({ delay: 3000, stopOnInteraction: true })]
  )
  
  const [instantPlayPrevEnabled, setInstantPlayPrevEnabled] = useState(true)
  const [instantPlayNextEnabled, setInstantPlayNextEnabled] = useState(true)
  
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])
  
  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])
  
  // Instant Play carousel - start in middle of duplicated items for bi-directional scroll
  useEffect(() => {
    if (!instantPlayEmblaApi) return
    // Scroll to middle set on init for seamless bi-directional scrolling
    const middleIndex = playableGames.length
    instantPlayEmblaApi.scrollTo(middleIndex, true)
  }, [instantPlayEmblaApi])

  return (
    <>
      <SEO 
        title={null}
        description="Play free browser & downloadable games instantly. One platform, zero limits. Arcade, puzzle, racing and more!"
        url="/"
      />
      <WebsiteStructuredData />
      
      <div className="bg-background-dark">
        {/* Hero Section */}
        <section className="relative min-h-screen flex flex-col items-center justify-center pt-24 pb-12 overflow-hidden">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/videos/hero-banner.mp4"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="absolute inset-0 bg-background-dark/70" />
          <div className="absolute inset-0 pointer-events-none opacity-50 bg-[linear-gradient(to_right,rgba(227,255,4,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(227,255,4,0.05)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-game-primary/10 blur-[120px] rounded-full pointer-events-none"></div>
        
        <div className="container max-w-[960px] px-4 text-center z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-game-primary mb-8 tracking-widest uppercase">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-game-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-game-primary"></span>
            </span>
            Web-based High Performance
          </div>

          <h1 className="text-white text-3xl md:text-5xl font-black leading-[1.1] tracking-tight mb-6">
            Browser & Downloadable Games.
            <span className="text-game-primary"> One Platform, Zero Limits.</span>
          </h1>
          
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Experience the next generation of social gaming directly in your browser. Play instantly, or download and install for a seamless, high-performance experience.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/games" className="w-full sm:w-auto bg-game-primary text-black font-black px-10 py-4 rounded-xl text-lg hover:scale-105 transition-transform text-center">
              Start Playing Now
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Experiences Section */}
      <section className="py-24 bg-background-dark relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-20">
          <div className="flex items-center justify-between mb-6 sm:mb-10">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-black tracking-tight">Featured Experiences</h2>
              <p className="text-slate-500 mt-1 sm:mt-2 text-sm sm:text-base">Hand-picked cinematic masterpieces from our community.</p>
            </div>
            <div className="hidden sm:flex gap-2">
              <button 
                onClick={scrollPrev}
                className="p-2 rounded-full border border-border-dark text-white hover:bg-white/5 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button 
                onClick={scrollNext}
                className="p-2 rounded-full border border-border-dark text-white hover:bg-white/5 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4 md:gap-6">
              {featuredGames.map((game, index) => (
                <Link 
                  key={game.id} 
                  to={getGameUrl(game)}
                  className="min-w-0 flex-[0_0_92%] sm:flex-[0_0_85%] md:flex-[0_0_75%] relative rounded-2xl md:rounded-3xl overflow-hidden group"
                >
                  <img 
                    alt={game.title} 
                    className="w-full aspect-[16/10] sm:aspect-[21/9] object-cover transition-transform duration-700 group-hover:scale-105" 
                    src={game.image}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 md:bottom-10 md:left-10 md:right-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div>
                      <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-game-primary text-black text-[8px] sm:text-[10px] font-black uppercase rounded mb-2 sm:mb-4 inline-block">
                        {game.tag}
                      </span>
                      <h3 className="text-white text-xl sm:text-2xl md:text-4xl font-black mb-1 sm:mb-2">{game.title}</h3>
                      <p className="text-slate-300 text-xs sm:text-sm md:text-base max-w-md line-clamp-2">{game.shortDescription || game.description}</p>
                    </div>
                    <button className="bg-white text-black font-bold px-4 py-2 sm:px-6 sm:py-2.5 md:px-8 md:py-3 rounded-full flex items-center gap-2 text-sm sm:text-base w-fit">
                      <Play className="w-3 h-3 sm:w-4 sm:h-4 fill-black" /> Play Free
                    </button>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 border-y border-border-dark">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-20">
          <div className="text-center mb-20">
            <h2 className="text-white text-4xl font-black tracking-tight mb-4">Start Playing in Seconds</h2>
            <p className="text-slate-400">The most frictionless way to enjoy high-end gaming.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
            <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-dark to-transparent hidden md:block"></div>
            
            <div className="relative flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-card-dark border border-border-dark rounded-3xl flex items-center justify-center text-game-primary mb-8 z-10 shadow-2xl">
                <Search className="w-8 h-8" />
              </div>
              <h3 className="text-white text-xl font-bold mb-3">1. Select</h3>
              <p className="text-slate-400">Browse thousands of unique community-made experiences.</p>
            </div>

            <div className="relative flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-game-primary rounded-3xl flex items-center justify-center text-black mb-8 z-10 shadow-2xl shadow-game-primary/20">
                <Rocket className="w-8 h-8" />
              </div>
              <h3 className="text-white text-xl font-bold mb-3">2. Play</h3>
              <p className="text-slate-400">One click to launch in browser or download for offline play.</p>
            </div>

            <div className="relative flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-card-dark border border-border-dark rounded-3xl flex items-center justify-center text-game-primary mb-8 z-10 shadow-2xl">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-white text-xl font-bold mb-3">3. Connect</h3>
              <p className="text-slate-400">Invite friends, join guilds, and compete for the leaderboard.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Genres Section */}
      <section className="py-24 bg-background-dark/50">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-20">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="text-white text-3xl font-black tracking-tight">Explore Genres</h2>
              <p className="text-slate-500 mt-2">Find exactly what you want to play.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {genres.map((genre) => (
              <Link 
                key={genre.name}
                to={`/genre/${genre.name.toLowerCase()}`}
                className="bg-white/[0.03] backdrop-blur-sm border border-white/5 group p-6 rounded-2xl flex flex-col items-center justify-center gap-4 hover:bg-white/10 transition-all"
              >
                <genre.icon className="w-8 h-8 text-game-primary transition-transform group-hover:scale-110" />
                <span className="text-white font-bold text-sm tracking-wide">{genre.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Experiences Section */}
      <section id="games" className="py-24 bg-background-dark/50 scroll-mt-28">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-20">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-white text-3xl font-black tracking-tight flex items-center gap-3">
              <Gamepad2 className="w-8 h-8 text-game-primary" />
              Trending Experiences
            </h2>
            <Link to="/games" className="text-game-primary font-bold text-sm uppercase tracking-widest flex items-center gap-1 hover:underline">
              All Games <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {trendingGames.map((game) => (
              <Link 
                key={game.id}
                to={getGameUrl(game)}
                className="bg-card-dark rounded-2xl border border-border-dark overflow-hidden transition-all duration-300 hover:shadow-[0_0_20px_rgba(227,255,4,0.15)] hover:border-game-primary group"
              >
                <div 
                  className="aspect-video w-full bg-cover bg-center overflow-hidden"
                  style={{ backgroundImage: `url("${game.image}")` }}
                >
                  <div className="w-full h-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button className="bg-game-primary text-black font-bold px-6 py-2 rounded-full">Play</button>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="text-white font-bold">{game.title}</h3>
                    <span className="text-xs bg-white/5 px-2 py-0.5 rounded text-slate-400">{game.rating} ★</span>
                  </div>
                  <p className="text-slate-500 text-xs mb-4">{game.genre} • {game.activePlayers || game.players} Players</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Instant Play Games Section */}
      <section className="py-24 bg-gradient-to-b from-background-dark to-card-dark/30">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-20">
          <div className="flex items-center justify-between mb-8 sm:mb-12">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2 sm:gap-3">
                <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-game-primary" />
                Play Now
              </h2>
              <p className="text-slate-500 mt-1 sm:mt-2 text-sm sm:text-base">Jump right in! Play instantly in browser or download for offline access.</p>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <button 
                onClick={() => instantPlayEmblaApi?.scrollPrev()}
                className="w-10 h-10 rounded-full bg-card-dark border border-border-dark flex items-center justify-center text-white hover:bg-white/10 hover:border-game-primary transition-all disabled:opacity-30"
                disabled={!instantPlayPrevEnabled}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button 
                onClick={() => instantPlayEmblaApi?.scrollNext()}
                className="w-10 h-10 rounded-full bg-card-dark border border-border-dark flex items-center justify-center text-white hover:bg-white/10 hover:border-game-primary transition-all disabled:opacity-30"
                disabled={!instantPlayNextEnabled}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="overflow-hidden" ref={instantPlayEmblaRef}>
            <div className="flex gap-4 sm:gap-6">
              {infinitePlayableGames.map((game, index) => (
                <Link 
                  key={`${game.id}-${index}`}
                  to={getGameUrl(game)}
                  className="flex-[0_0_160px] sm:flex-[0_0_220px] bg-card-dark rounded-xl sm:rounded-2xl border border-border-dark overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(227,255,4,0.2)] hover:border-game-primary hover:scale-[1.02] group relative"
                >
                  {/* Instant Play Badge */}
                  <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 px-1.5 py-0.5 sm:px-2 sm:py-1 bg-green-500/90 backdrop-blur-sm rounded-full text-[8px] sm:text-[10px] font-black text-white flex items-center gap-1">
                    <Zap className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> PLAY NOW
                  </div>
                  
                  <div 
                    className="aspect-square w-full bg-cover bg-center overflow-hidden"
                    style={{ backgroundImage: `url("${game.image}")` }}
                  >
                    <div className="w-full h-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="bg-game-primary text-black font-bold px-4 py-2 sm:px-6 sm:py-3 rounded-full flex items-center gap-2 transform scale-90 group-hover:scale-100 transition-transform text-sm sm:text-base">
                        <Play className="w-4 h-4 sm:w-5 sm:h-5 fill-black" /> View
                      </div>
                    </div>
                  </div>
                  <div className="p-3 sm:p-4">
                    <h3 className="text-white font-bold mb-1 text-sm sm:text-base group-hover:text-game-primary transition-colors truncate">{game.title}</h3>
                    <div className="flex items-center justify-between">
                      <p className="text-slate-500 text-[10px] sm:text-xs">{game.genre}</p>
                      <span className="text-[10px] sm:text-xs text-game-primary">{game.rating} ★</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features Section */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="flex flex-col items-center text-center bg-game-primary/5 p-6 rounded-2xl border border-game-primary/20">
              <ShieldCheck className="w-10 h-10 text-game-primary mb-4" />
              <h4 className="text-white font-bold mb-2">Safety First</h4>
              <p className="text-slate-500 text-sm">Advanced moderation and privacy controls for all ages.</p>
            </div>
            <div className="flex flex-col items-center text-center bg-game-primary/5 p-6 rounded-2xl border border-game-primary/20">
              <Wallet className="w-10 h-10 text-game-primary mb-4" />
              <h4 className="text-white font-bold mb-2">Fair Economy</h4>
              <p className="text-slate-500 text-sm">Transparent creator payouts and easy transactions.</p>
            </div>
            <div className="flex flex-col items-center text-center bg-game-primary/5 p-6 rounded-2xl border border-game-primary/20">
              <Globe className="w-10 h-10 text-game-primary mb-4" />
              <h4 className="text-white font-bold mb-2">Open Platform</h4>
              <p className="text-slate-500 text-sm">No gatekeepers. Anyone can publish and reach players.</p>
            </div>
            <div className="flex flex-col items-center text-center bg-game-primary/5 p-6 rounded-2xl border border-game-primary/20">
              <Network className="w-10 h-10 text-game-primary mb-4" />
              <h4 className="text-white font-bold mb-2">Interconnected</h4>
              <p className="text-slate-500 text-sm">Carry your avatar and items across every experience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section className="py-24 bg-black relative">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-game-primary/5 p-8 rounded-2xl border border-game-primary/20">
                  <h4 className="text-game-primary font-black text-2xl mb-2">WebGL 3</h4>
                  <p className="text-slate-400 text-sm">Cutting-edge rendering directly in the browser tab.</p>
                </div>
                <div className="bg-game-primary/5 p-8 rounded-2xl border border-game-primary/20">
                  <h4 className="text-game-primary font-black text-2xl mb-2">WASM</h4>
                  <p className="text-slate-400 text-sm">Near-native execution speed for complex logic.</p>
                </div>
                <div className="bg-game-primary/5 p-8 rounded-2xl border border-game-primary/20">
                  <h4 className="text-game-primary font-black text-2xl mb-2">uWebSocket</h4>
                  <p className="text-slate-400 text-sm">Ultra-low latency multiplayer synchronization.</p>
                </div>
                <div className="bg-game-primary/5 p-8 rounded-2xl border border-game-primary/20">
                  <h4 className="text-game-primary font-black text-2xl mb-2">Edge Cast</h4>
                  <p className="text-slate-400 text-sm">Global server mesh for sub-20ms ping worldwide.</p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-white text-4xl font-black mb-6 leading-tight">
                Architecture of the <span className="text-game-primary">Next Web</span>
              </h2>
              <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                We've spent 4 years re-engineering the browser's potential. Our stack combines high-performance C++ core with modern web standards to deliver console-quality gaming without the hardware.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-white font-medium">
                  <CheckCircle2 className="w-5 h-5 text-game-primary" />
                  No proprietary plugins required
                </li>
                <li className="flex items-center gap-3 text-white font-medium">
                  <CheckCircle2 className="w-5 h-5 text-game-primary" />
                  Cross-platform by default (Mobile/Desktop)
                </li>
                <li className="flex items-center gap-3 text-white font-medium">
                  <CheckCircle2 className="w-5 h-5 text-game-primary" />
                  Infinite scalability via cloud-native backend
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-20">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-white text-3xl font-black tracking-tight">Latest News</h2>
            <Link to="/blogs" className="text-slate-400 hover:text-white font-bold transition-colors">View Blog</Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {latestBlogs.map((blog) => (
              <Link key={blog.slug} to={`/blogs/${blog.slug}`} className="group">
                <article>
                  <div className="aspect-video rounded-2xl overflow-hidden mb-6 bg-card-dark border border-border-dark">
                    {blog.coverImage ? (
                      <img
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        src={blog.coverImage}
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-white/5" />
                    )}
                  </div>

                  <div className="text-slate-500 text-[10px] font-black uppercase mb-3">
                    {blog.publishedAt
                      ? new Date(blog.publishedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                      : 'New'}
                  </div>
                  <h3 className="text-white text-lg leading-none font-bold mb-3 group-hover:text-game-primary transition-colors">
                    {blog.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">
                    {blog.excerpt}
                  </p>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-background-dark/30">
        <div className="container max-w-[800px] mx-auto px-4 lg:px-20">
          <h2 className="text-white text-3xl font-black text-center mb-12">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFaq === index}
                onToggle={() => setOpenFaq(openFaq === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </section>
      </div>
    </>
  )
}
